import React, {FC, ReactElement, ReactNode} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faInfoCircle} from '@fortawesome/free-solid-svg-icons'

// Shared overlay-tooltip helpers for the OMS create/edit order pages.
// Uses the same react-bootstrap OverlayTrigger + Tooltip pattern as the orders list page.

let tooltipSeq = 0
const nextTooltipId = () => `oms-tt-${++tooltipSeq}`

type OmsTooltipProps = {
  tip?: ReactNode
  placement?: any
  /** Wrap as inline-flex (buttons) vs block (full-width fields). Defaults to inline. */
  inline?: boolean
  children: ReactElement
}

// Wraps any element (typically an action button) with a hover/focus tooltip.
// The extra span anchor guarantees the tooltip still shows for disabled controls,
// which otherwise swallow pointer events.
export const OmsTooltip: FC<OmsTooltipProps> = ({tip, placement = 'top', inline = true, children}) => {
  if (!tip) return children
  return (
    <OverlayTrigger placement={placement} overlay={<Tooltip id={nextTooltipId()}>{tip}</Tooltip>}>
      <span className='oms-tt-anchor' style={{display: inline ? 'inline-flex' : 'block'}}>
        {children}
      </span>
    </OverlayTrigger>
  )
}

type OmsInfoLabelProps = {
  text: ReactNode
  tip?: ReactNode
  required?: boolean
  className?: string
  placement?: any
}

// Field label with an optional info-circle that reveals a descriptive overlay tooltip.
export const OmsInfoLabel: FC<OmsInfoLabelProps> = ({
  text,
  tip,
  required = false,
  className = 'oms-label',
  placement = 'top',
}) => (
  <label className={className}>
    {text}
    {required && <span className='text-red-500'>*</span>}
    {tip && (
      <OverlayTrigger placement={placement} overlay={<Tooltip id={nextTooltipId()}>{tip}</Tooltip>}>
        <span
          className='oms-label-info'
          style={{marginLeft: 6, cursor: 'help', color: 'var(--muted)'}}
        >
          <FontAwesomeIcon icon={faInfoCircle} />
        </span>
      </OverlayTrigger>
    )}
  </label>
)

// Inline text (table headers, totals row labels) with optional info-circle overlay tooltip.
export const OmsInfoText: FC<OmsInfoLabelProps> = ({
  text,
  tip,
  required = false,
  className,
  placement = 'top',
}) => (
  <span className={className}>
    {text}
    {required && <span className='text-red-500 ml-1'>*</span>}
    {tip && (
      <OverlayTrigger placement={placement} overlay={<Tooltip id={nextTooltipId()}>{tip}</Tooltip>}>
        <span
          className='oms-label-info'
          style={{marginLeft: 6, cursor: 'help', color: 'var(--muted)'}}
        >
          <FontAwesomeIcon icon={faInfoCircle} />
        </span>
      </OverlayTrigger>
    )}
  </span>
)
